import { evenPercents, MAX_COLUMNS } from "../column-presets";
import { applyRowColumnContentRebalance } from "../column-content-min";
import { findColumn, findGroup, findRow, locateRow } from "../find";
import { isGroup } from "../sections";
import {
  cloneBlock,
  cloneColumn,
  cloneGroup,
  cloneRow,
} from "../factories";
import {
  canMoveBlockInColumn,
  canMoveColumnInRow,
  canMoveGroup,
  canMoveRowSelection,
  isBlockSelection,
} from "../selection/ops";
import { sanitizeFillColumnImagesInRow } from "../image-height";
import type { EditorDraft } from "./types";
import { applySelectionChange } from "./draft";
import { deleteRowOrClear } from "./deletes";
import { moveRowInGroup } from "./moves";
import { removeColumn } from "./columns";
import {
  copyBlocksForClipboard,
  cutSelectedBlocks,
  deleteBlocksInColumn,
  duplicateBlocksInColumn,
  moveContiguousBlocksInColumnByDelta,
  pasteBlocksFromClipboard,
} from "./bulk-block-operations";
import {
  deleteTopLevelRows,
  duplicateTopLevelRows,
  moveContiguousSiblingRowsByDelta,
} from "./bulk-row-operations";
import type { Block } from "../types";

function selectedBlockIdsForBulkAction(draft: EditorDraft): string[] {
  if (!isBlockSelection(draft.selection)) {
    return [];
  }

  if (draft.selectedBlockIds.length > 1) {
    return draft.selectedBlockIds;
  }

  return [draft.selection.blockId];
}

function selectedRowIdsForBulkAction(draft: EditorDraft): string[] {
  if (draft.selection.kind !== "row") {
    return [];
  }

  const selectedRowIds = draft.selectedRowIds ?? [];
  if (selectedRowIds.length > 1) {
    return selectedRowIds;
  }

  return [draft.selection.rowId];
}

export function duplicateSelected(draft: EditorDraft): boolean {
  const { selection, document: doc } = draft;
  if (isBlockSelection(selection)) {
    const blockIds = selectedBlockIdsForBulkAction(draft);
    if (blockIds.length > 1) {
      return duplicateBlocksInColumn(
        draft,
        selection.rowId,
        selection.columnId,
        blockIds,
      );
    }

    const column = findColumn(doc, selection.rowId, selection.columnId);
    if (!column) {
      return false;
    }
    const index = column.blocks.findIndex(
      block => block.id === selection.blockId,
    );
    const source = column.blocks[index];
    if (!source || index < 0) {
      return false;
    }
    const copy = cloneBlock(source);
    column.blocks.splice(index + 1, 0, copy);
    const row = findRow(doc, selection.rowId);
    if (row) {
      sanitizeFillColumnImagesInRow(row);
      applyRowColumnContentRebalance(draft.document, selection.rowId);
    }
    applySelectionChange(draft, {
      kind: "block",
      rowId: selection.rowId,
      columnId: selection.columnId,
      blockId: copy.id,
    });
    return true;
  }
  if (selection.kind === 'column') {
    const row = findRow(doc, selection.rowId);
    if (!row || row.columns.length >= MAX_COLUMNS) {
      return false;
    }
    const index = row.columns.findIndex(
      column => column.id === selection.columnId,
    );
    const source = row.columns[index];
    if (!source || index < 0) {
      return false;
    }
    const copy = cloneColumn(source);
    row.columns.splice(index + 1, 0, copy);
    row.columnWidths = evenPercents(row.columns.length);
    if (row.columns.length > 1 && row.columnGap === 0) {
      row.columnGap = 16;
    }
    draft.selection = { kind: 'column', rowId: row.id, columnId: copy.id };
    return true;
  }
  if (selection.kind === 'group') {
    const index = doc.sections.findIndex(
      section => section.id === selection.groupId,
    );
    const source = doc.sections[index];
    if (!source || index < 0 || !isGroup(source)) {
      return false;
    }
    const copy = cloneGroup(source);
    doc.sections.splice(index + 1, 0, copy);
    draft.selection = { kind: 'group', groupId: copy.id };
    return true;
  }
  if (selection.kind === 'row') {
    const rowIds = selectedRowIdsForBulkAction(draft);
    if (rowIds.length > 1) {
      return duplicateTopLevelRows(draft, rowIds);
    }

    const located = locateRow(doc.sections, selection.rowId);
    if (!located) {
      return false;
    }
    const copy = cloneRow(located.row);
    if (located.location.parent === 'document') {
      doc.sections.splice(located.location.sectionIndex + 1, 0, copy);
    } else {
      const group = findGroup(doc.sections, located.location.groupId);
      if (!group) {
        return false;
      }
      group.rows.splice(located.location.rowIndex + 1, 0, copy);
    }
    applySelectionChange(draft, { kind: 'row', rowId: copy.id });
    return true;
  }
  return false;
}

export function deleteSelected(draft: EditorDraft): boolean {
  const { selection, document: doc } = draft;
  if (isBlockSelection(selection)) {
    const blockIds = selectedBlockIdsForBulkAction(draft);
    if (blockIds.length > 1) {
      return deleteBlocksInColumn(
        draft,
        selection.rowId,
        selection.columnId,
        blockIds,
      );
    }

    const column = findColumn(doc, selection.rowId, selection.columnId);
    if (!column) {
      return false;
    }
    const index = column.blocks.findIndex(
      block => block.id === selection.blockId,
    );
    if (index < 0) {
      return false;
    }
    column.blocks.splice(index, 1);
    const nextBlock = column.blocks[index] ?? column.blocks[index - 1];
    applySelectionChange(
      draft,
      nextBlock
        ? {
            kind: 'block',
            rowId: selection.rowId,
            columnId: selection.columnId,
            blockId: nextBlock.id,
          }
        : {
            kind: 'column',
            rowId: selection.rowId,
            columnId: selection.columnId,
          },
    );
    return true;
  }
  if (selection.kind === 'column') {
    const row = findRow(doc, selection.rowId);
    if (!row) {
      return false;
    }
    if (row.columns.length > 1) {
      return removeColumn(draft, selection.rowId, selection.columnId);
    }
    return deleteRowOrClear(draft, selection.rowId);
  }
  if (selection.kind === 'group') {
    return false;
  }
  if (selection.kind === 'row') {
    const rowIds = selectedRowIdsForBulkAction(draft);
    if (rowIds.length > 1) {
      return deleteTopLevelRows(draft, rowIds);
    }
    return deleteRowOrClear(draft, selection.rowId);
  }
  return false;
}

export function moveSelected(draft: EditorDraft, direction: -1 | 1): boolean {
  const { selection, document: doc } = draft;
  if (isBlockSelection(selection)) {
    const blockIds = selectedBlockIdsForBulkAction(draft);
    if (blockIds.length > 1) {
      return moveContiguousBlocksInColumnByDelta(
        draft,
        selection.rowId,
        selection.columnId,
        blockIds,
        direction,
      );
    }

    if (!canMoveBlockInColumn(doc, selection, direction)) {
      return false;
    }
    const column = findColumn(doc, selection.rowId, selection.columnId);
    if (!column) {
      return false;
    }
    const index = column.blocks.findIndex(
      block => block.id === selection.blockId,
    );
    const nextIndex = index + direction;
    const [moved] = column.blocks.splice(index, 1);
    if (moved) {
      column.blocks.splice(nextIndex, 0, moved);
    }
    return true;
  }
  if (selection.kind === 'column') {
    if (!canMoveColumnInRow(doc, selection, direction)) {
      return false;
    }
    const row = findRow(doc, selection.rowId);
    if (!row) {
      return false;
    }
    const index = row.columns.findIndex(
      column => column.id === selection.columnId,
    );
    const nextIndex = index + direction;
    const [movedCol] = row.columns.splice(index, 1);
    const [movedWidth] = row.columnWidths.splice(index, 1);
    if (movedCol) {
      row.columns.splice(nextIndex, 0, movedCol);
    }
    row.columnWidths.splice(nextIndex, 0, movedWidth ?? evenPercents(row.columns.length)[nextIndex] ?? 1);
    return true;
  }
  if (selection.kind === 'group') {
    if (!canMoveGroup(doc, selection, direction)) {
      return false;
    }
    const index = doc.sections.findIndex(
      section => section.id === selection.groupId,
    );
    const nextIndex = index + direction;
    const [moved] = doc.sections.splice(index, 1);
    if (moved) {
      doc.sections.splice(nextIndex, 0, moved);
    }
    return true;
  }
  if (selection.kind === 'row') {
    const rowIds = selectedRowIdsForBulkAction(draft);
    if (rowIds.length > 1) {
      return moveContiguousSiblingRowsByDelta(draft, rowIds, direction);
    }

    const located = locateRow(doc.sections, selection.rowId);
    if (!located) {
      return false;
    }
    if (located.location.parent === 'group') {
      if (!canMoveRowSelection(doc, selection, direction)) {
        return false;
      }
      return moveRowInGroup(
        draft,
        located.location.groupId,
        located.location.rowIndex,
        located.location.rowIndex + direction,
      );
    }
    if (!canMoveRowSelection(doc, selection, direction)) {
      return false;
    }
    const nextIndex = located.location.sectionIndex + direction;
    const [moved] = doc.sections.splice(located.location.sectionIndex, 1);
    if (moved) {
      doc.sections.splice(nextIndex, 0, moved);
    }
    return true;
  }
  return false;
}

export function copySelectedBlocks(draft: EditorDraft): Block[] {
  const blockIds = selectedBlockIdsForBulkAction(draft);
  if (blockIds.length === 0) {
    return [];
  }
  return copyBlocksForClipboard(draft.document, blockIds);
}

export function cutSelectedBlocksToClipboard(draft: EditorDraft): Block[] {
  const blockIds = selectedBlockIdsForBulkAction(draft);
  if (blockIds.length === 0) {
    return [];
  }
  return cutSelectedBlocks(draft, blockIds);
}

export function pasteBlocksFromEditorClipboard(
  draft: EditorDraft,
  clipboardBlocks: Block[],
): boolean {
  return pasteBlocksFromClipboard(draft, clipboardBlocks);
}
